const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_DATA_URL_LENGTH = 174786;

export async function prepareSignatureImage(file) {
  if (!file || !file.size) throw new Error("Choisissez une image contenant votre signature.");
  if (!/^image\/(png|jpeg|webp)$/.test(file.type)) throw new Error("Formats acceptés : PNG, JPEG et WebP.");
  if (file.size > MAX_FILE_BYTES) throw new Error("L'image dépasse 5 Mo. Choisissez une image plus légère.");
  const source = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Impossible de lire ce fichier. Choisissez une autre image."));
    reader.readAsDataURL(file);
  });
  const image = await new Promise((resolve, reject) => {
    const element = new Image();
    element.onload = () => resolve(element);
    element.onerror = () => reject(new Error("Cette image est illisible. Essayez un autre fichier PNG, JPEG ou WebP."));
    element.src = source;
  });
  if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth * image.naturalHeight > 20000000) {
    throw new Error("Les dimensions de l'image sont trop grandes. Recadrez-la sur votre signature.");
  }
  const working = document.createElement("canvas");
  const scale = Math.min(1, 1600 / image.naturalWidth, 1600 / image.naturalHeight);
  working.width = Math.max(1, Math.round(image.naturalWidth * scale));
  working.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const context = working.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("L'import d'image n'est pas disponible dans ce navigateur. Vous pouvez dessiner votre signature.");
  context.drawImage(image, 0, 0, working.width, working.height);
  const pixels = context.getImageData(0, 0, working.width, working.height);
  let left = working.width, top = working.height, right = -1, bottom = -1, ink = 0;
  for (let y = 0; y < working.height; y += 1) {
    for (let x = 0; x < working.width; x += 1) {
      const index = (y * working.width + x) * 4;
      const alpha = pixels.data[index + 3] / 255;
      const visible = (255 - Math.min(pixels.data[index], pixels.data[index + 1], pixels.data[index + 2])) * alpha > 10;
      if (!visible) { pixels.data[index + 3] = 0; continue; }
      left = Math.min(left, x); top = Math.min(top, y);
      right = Math.max(right, x); bottom = Math.max(bottom, y); ink += 1;
    }
  }
  if (ink < 20 || right - left < 3 || bottom - top < 1) {
    throw new Error("La signature n'est pas assez visible. Choisissez une signature sur fond blanc ou transparent.");
  }
  context.putImageData(pixels, 0, 0);
  const width = right - left + 1, height = bottom - top + 1;
  const output = document.createElement("canvas");
  let targetScale = Math.min(1, 900 / width, 300 / height);
  for (let attempt = 0; attempt < 6; attempt += 1) {
    output.width = Math.max(1, Math.round(width * targetScale));
    output.height = Math.max(1, Math.round(height * targetScale));
    output.getContext("2d").drawImage(working, left, top, width, height, 0, 0, output.width, output.height);
    const dataUrl = output.toDataURL("image/png");
    if (dataUrl.length <= MAX_DATA_URL_LENGTH) return { dataUrl, name: file.name };
    targetScale *= 0.75;
  }
  throw new Error("L'image est trop complexe. Recadrez-la sur votre signature et réessayez.");
}
